import { Inject, Injectable } from '@angular/core';
import { CoolHttp } from '@angular-cool/http';
import { Store } from '@ngxs/store';
import { CoolLocalStorage } from '@angular-cool/storage';
import { Environment } from '../../../environments/environment.interface';
import { ENVIRONMENT, WINDOW } from './injection-tokens';
import { jwtDecode } from 'jwt-decode';
import { lastValueFrom } from 'rxjs';
import { AuthToken, AuthTokenPayload, OneTimeAuthToken } from '../../../../../common/dto/auth.dto';
import { ApplicationComponent } from '../../../../../common/dto/app.dto';
import { SetSessionAction } from '../../app.state';
import { UserSession } from '../../../../../common/dto/user-session.dto';
import { ServerUrls } from '../../../../../common/dto/server-urls';

export const API_TOKEN_STORAGE_KEY = 'rpg-bench-token';

@Injectable()
export class AuthenticationService {
  constructor(
    private _store: Store,
    private _http: CoolHttp,
    private _localStorage: CoolLocalStorage,
    @Inject(ENVIRONMENT) private _environment: Environment,
    @Inject(WINDOW) private _window: Window,
  ) {}

  public async initializeAsync() {
    const savedApiToken = <AuthToken | undefined | null>this._localStorage.getItem(API_TOKEN_STORAGE_KEY);

    if (savedApiToken) {
      this._setApiToken(savedApiToken);
    }

    await this.refreshUserSessionAsync();
  }

  public async goToLoginAsync() {
    this._window.location.href =
      this._environment.appComponentUrls[ApplicationComponent.Login] + `?app=${ApplicationComponent.App}`;
  }

  public async goToRegistrationAsync() {
    this._window.location.href =
      this._environment.appComponentUrls[ApplicationComponent.Login] + `/registration?app=${ApplicationComponent.App}`;
  }

  public async createSessionFromOneTimeAuthTokenAsync(oneTimeAuthToken: OneTimeAuthToken) {
    let response: any = undefined;

    try {
      response = await this._http.postAsync<{
        token: string;
      }>(ServerUrls.Authentications.children.Session.children.FromOneTimeToken.absoluteUrl, {
        token: oneTimeAuthToken,
        component: ApplicationComponent.App,
      });

      await this._setSessionFromTokenResponseAsync(response);
    } catch {}
  }

  public async refreshUserSessionAsync() {
    let response: any = undefined;

    try {
      response = await this._http.postAsync<{
        token: string;
      }>(ServerUrls.Authentications.children.Session.children.CreateNew.absoluteUrl, {
        component: ApplicationComponent.App,
      });
    } catch {}

    await this._setSessionFromTokenResponseAsync(response);
  }

  public async logoutAsync() {
    await this._http.postAsync(ServerUrls.Authentications.children.Logout.absoluteUrl);

    this._localStorage.removeItem(API_TOKEN_STORAGE_KEY);

    this._http.deregisterGlobalHeader('Authorization');

    await lastValueFrom(this._store.dispatch(new SetSessionAction(undefined)));

    this._window.location.href =
      this._environment.appComponentUrls[ApplicationComponent.Login] + '/logout' + `?app=${ApplicationComponent.App}`;
  }

  private async _setSessionFromTokenResponseAsync(response: { token: AuthToken }) {
    let session: UserSession | undefined = undefined;

    if (response?.token) {
      session = <UserSession>jwtDecode<AuthTokenPayload>(response.token)?.session;
    }

    this._setApiToken(response?.token);

    await lastValueFrom(this._store.dispatch(new SetSessionAction(session)));
  }

  private _setApiToken(token: AuthToken | undefined) {
    this._localStorage.setItem(API_TOKEN_STORAGE_KEY, token || '');

    if (token) {
      this._http.registerGlobalHeader({
        key: 'Authorization',
        value: `Bearer ${token}`,
      });
    } else {
      this._http.deregisterGlobalHeader('Authorization');
    }
  }
}
